// Wire types — must stay in sync with backend/app/schemas/*.

export type UUID = string;
export type ISODateTime = string;

// ---------- common ----------

export interface Pagination {
  page: number;
  page_size: number;
  total: number;
  pages: number;
}

export interface Page<T> {
  items: T[];
  pagination: Pagination;
}

export interface ErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown> | null;
}

export interface ErrorResponse {
  error: ErrorBody;
}

// ---------- enums (string unions, must match backend) ----------

export const FlowType = ["INCOMING", "INTERNAL", "OUTGOING"] as const;
export type FlowType = (typeof FlowType)[number];

export const FlowStatus = [
  "PENDING",
  "IN_PROGRESS",
  "ACCEPTED",
  "REJECTED",
  "DISPOSED",
] as const;
export type FlowStatus = (typeof FlowStatus)[number];

export const OperationType = [
  "PRODUCTION",
  "QC",
  "ACCUMULATION",
  "TECHNOLOGICAL",
  "PRINTING",
] as const;
export type OperationType = (typeof OperationType)[number];

export const IdentificationMethod = ["LABEL", "RFID", "SERIAL", "NONE"] as const;
export type IdentificationMethod = (typeof IdentificationMethod)[number];

export const FlowDirection = ["INPUT", "OUTPUT"] as const;
export type FlowDirection = (typeof FlowDirection)[number];

export const OnRejectAction = ["DISPOSE", "SIGNAL", "REWORK"] as const;
export type OnRejectAction = (typeof OnRejectAction)[number];

export const OrderStatus = [
  "DRAFT",
  "ACTIVE",
  "ADJUSTED",
  "COMPLETED",
  "CANCELLED",
] as const;
export type OrderStatus = (typeof OrderStatus)[number];

export const ExecutionStatus = [
  "STARTED",
  "IN_PROGRESS",
  "QC_PENDING",
  "COMPLETED",
  "FAILED",
] as const;
export type ExecutionStatus = (typeof ExecutionStatus)[number];

export const QCResult = ["ACCEPTED", "REJECTED"] as const;
export type QCResult = (typeof QCResult)[number];

export const ScanType = ["RFID", "NFC", "QR", "BARCODE"] as const;
export type ScanType = (typeof ScanType)[number];

export const DeviceStatus = ["ONLINE", "OFFLINE", "ERROR"] as const;
export type DeviceStatus = (typeof DeviceStatus)[number];

/** Код роли — строка таблицы roles на бэкенде (роли — данные установки). */
export type OperatorRole = string;

// ---------- admin (users / roles / permissions) ----------

export interface AdminUserRead {
  id: UUID;
  name: string;
  role: OperatorRole;
  login: string | null;
  nfc_tag: string | null;
  is_active: boolean;
  allowed_stage_ids: number[];
  allowed_device_ids: UUID[];
  last_login_at: ISODateTime | null;
  must_change_password: boolean;
  created_at: ISODateTime | null;
  updated_at: ISODateTime | null;
}

export interface AdminUserCreate {
  name: string;
  role: OperatorRole;
  login?: string | null;
  password?: string | null;
  nfc_tag?: string | null;
  pin?: string | null;
  is_active?: boolean;
  allowed_stage_ids?: number[];
  allowed_device_ids?: UUID[];
  must_change_password?: boolean;
}

export interface AdminUserUpdate {
  name?: string;
  role?: OperatorRole;
  login?: string | null;
  nfc_tag?: string | null;
  is_active?: boolean;
}

export interface AdminResetPassword {
  new_password: string;
  force_change?: boolean;
}

export interface AdminResetPin {
  new_pin: string;
}

export interface AdminRotateNfc {
  new_nfc_tag: string;
}

export interface AdminAssignStages {
  allowed_stage_ids: number[];
}

export interface AdminAssignDevices {
  allowed_device_ids: UUID[];
}

export interface AdminPermissionRead {
  code: string;
  description: string | null;
}

export interface AdminRoleRead {
  role: OperatorRole;
  name: string;
  is_system: boolean;
  permissions: string[];
  user_count: number;
}

export interface AdminRoleCreate {
  code: string;
  name: string;
}

export interface AdminRolePermissionsUpdate {
  permissions: string[];
}

// ---------- admin: audit ----------

export interface AuditEntry {
  id: number;
  entity_type: string;
  entity_id: UUID;
  action: string;
  actor_id: UUID | null;
  changes: Record<string, unknown> | null;
  created_at: ISODateTime;
}

export interface AuditFacets {
  actions: string[];
  entity_types: string[];
}

// ---------- stations ----------

export interface StationRead {
  id: number;
  code: string;
  name: string;
  stage_id: number | null;
  is_active: boolean;
  created_at: ISODateTime | null;
  updated_at: ISODateTime | null;
}

export interface StationReadDetailed extends StationRead {
  operator_count: number;
}

export interface StationCreate {
  code: string;
  name: string;
  stage_id?: number | null;
  is_active?: boolean;
}

export interface StationUpdate {
  code?: string;
  name?: string;
  stage_id?: number | null;
  is_active?: boolean;
}

export interface StationAssignOperators {
  operator_ids: UUID[];
}

export interface StationAssignmentRead {
  operator_id: UUID;
  station_id: number;
  status: string;
  assigned_by: UUID | null;
  assigned_at: ISODateTime;
  revoked_at: ISODateTime | null;
}

export interface StationAssignmentReadDetailed extends StationAssignmentRead {
  operator_name: string;
  operator_login: string | null;
  operator_role: string;
  station_code: string;
  station_name: string;
}

export interface AdminAssignStations {
  station_ids: number[];
}

// ---------- stages ----------

export interface ExceptionSignalDef {
  code: string;
  label_ru: string;
}

export interface StageConfig {
  exception_signals?: ExceptionSignalDef[];
  per_item_label_kind?: string | null;
  batch_label_kind?: string | null;
  /** Расход материалов на 1 единицу выхода. Ключ — code материала из
   *  inventory_materials (MS, FS, FST, …), значение — qty per unit. */
  material_consumption?: Record<string, number> | null;
  /** Чеклист «что проверить» на per-item QC экране мобилы. */
  qc_checklist?: { text: string }[];
  /** Подсказка над списком сканируемых материалов на экране запуска. */
  materials_hint?: string | null;
  /** Причины недостачи при закрытии партии (мобила). */
  shortfall_reasons?: ExceptionSignalDef[];
  [k: string]: unknown;
}

export interface OperationPrintConfig {
  template_kind?: string;
  default_agent_id?: string | null;
  default_printer_name?: string | null;
}

export interface OperationConfig {
  print?: OperationPrintConfig;
  [k: string]: unknown;
}

export interface StageRead {
  id: number;
  code: string;
  /** Display name resolved by the backend for the request locale (i18n S3).
   *  Prefer this over name_ru/name_en (kept for dual-read during migration). */
  name?: string;
  name_key?: string | null;
  name_ru: string;
  name_en: string | null;
  is_active: boolean;
  identification: string | null;
  config: StageConfig | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

// Body of PATCH /admin/cycle/stages/:id. Null on a known key removes
// it from the stored blob; absent key leaves it untouched.
export interface StageConfigPatch {
  exception_signals?: ExceptionSignalDef[];
  per_item_label_kind?: string | null;
  batch_label_kind?: string | null;
  material_consumption?: Record<string, number> | null;
  qc_checklist?: { text: string }[] | null;
  materials_hint?: string | null;
  shortfall_reasons?: ExceptionSignalDef[] | null;
  [k: string]: unknown;
}

export interface OperationConfigPatch {
  name_ru?: string;
  print?: OperationPrintConfig;
  [k: string]: unknown;
}

export interface OperationRead {
  id: UUID;
  stage_id: number;
  code: string;
  /** Locale-resolved display name (i18n S3); name_ru kept for dual-read. */
  name?: string;
  name_key?: string | null;
  name_ru: string;
  type: OperationType;
  equipment_code: string | null;
  document_code: string | null;
  transformation_rule: string | null;
  config: Record<string, unknown> | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface StageReadDetailed extends StageRead {
  config: Record<string, unknown>;
  operations: OperationRead[];
}

// ---------- admin: cycle constructor (S2.x) ----------
// Mirrors backend POST bodies + the declarative process graph returned
// by GET /admin/cycle. Used by the cycle editor's constructor UI.

export interface CreateStageBody {
  code: string;
  name_ru: string;
  name_en?: string | null;
  identification?: IdentificationMethod | null;
  config?: Record<string, unknown> | null;
}

export interface StageCreated {
  id: number;
  code: string;
  name_ru: string;
  name_en: string | null;
  is_active: boolean;
  identification: string | null;
  config: Record<string, unknown> | null;
}

export interface CreateOperationBody {
  stage_id: number;
  code: string;
  name_ru: string;
  type: OperationType;
  equipment_code?: string | null;
  document_code?: string | null;
  transformation_rule?: string | null;
  inputs?: string[];
  outputs?: string[];
  on_reject?: OnRejectAction | null;
  exception_code?: string | null;
  accumulation?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
}

export interface OperationCreated {
  id: UUID;
  stage_id: number;
  code: string;
  name_ru: string;
  type: string;
  inputs: string[];
  outputs: string[];
  on_reject: string | null;
  exception_code: string | null;
  accumulation: Record<string, unknown> | null;
}

export interface CreateFlowBody {
  stage_id: number;
  code: string;
  direction: FlowDirection;
  flow_type: FlowType;
  endpoint?: string | null;
  name_ru?: string | null;
  name_en?: string | null;
}

export interface FlowCreated {
  id: UUID;
  stage_id: number;
  code: string;
  direction: string;
  flow_type: string;
  endpoint: string | null;
  name_ru: string | null;
  name_en: string | null;
}

// ---------- admin: metric definitions (метрики изделия) ----------
// Declarative catalogue of measurable product / test metrics. Backend
// under permission cycle.manage, base /admin/metrics.

export const MetricValueType = ["number", "enum", "bool", "string"] as const;
export type MetricValueType = (typeof MetricValueType)[number];

export const MetricScope = ["product", "test"] as const;
export type MetricScope = (typeof MetricScope)[number];

export interface MetricDefinition {
  id: UUID;
  key: string;
  name_ru: string;
  name_en: string | null;
  unit: string | null;
  value_type: MetricValueType;
  min_value: number | null;
  max_value: number | null;
  enum_values: string[] | null;
  scope: MetricScope;
  sort_order: number;
  enabled: boolean;
}

export interface CreateMetricBody {
  key: string;
  name_ru: string;
  name_en?: string | null;
  unit?: string | null;
  value_type?: MetricValueType;
  min_value?: number | null;
  max_value?: number | null;
  enum_values?: string[] | null;
  scope?: MetricScope;
  sort_order?: number;
  enabled?: boolean;
}

export interface UpdateMetricBody {
  key?: string;
  name_ru?: string;
  name_en?: string | null;
  unit?: string | null;
  value_type?: MetricValueType;
  min_value?: number | null;
  max_value?: number | null;
  enum_values?: string[] | null;
  scope?: MetricScope;
  sort_order?: number;
  enabled?: boolean;
}

// --- declarative process graph (GET /admin/cycle) ---

/** Per-locale name map { ru, en, zh, … } from the cycle graph, for the
 *  admin editor's per-locale fields (i18n S3.2). */
export type LocaleNames = Record<string, string>;

export interface ProcessFlowView {
  id: UUID;
  code: string;
  direction: string;
  flow_type: string;
  endpoint: string | null;
  /** Locale-resolved display name (i18n S3); name_ru kept for dual-read. */
  name?: string;
  name_key?: string | null;
  names?: LocaleNames;
  name_ru: string | null;
  name_en: string | null;
}

export interface ProcessOperationView {
  id: UUID;
  code: string;
  name?: string;
  name_key?: string | null;
  names?: LocaleNames;
  name_ru: string;
  type: string;
  equipment_code: string | null;
  document_code: string | null;
  transformation_rule: string | null;
  inputs: string[];
  outputs: string[];
  on_reject: string | null;
  exception_code: string | null;
  accumulation: Record<string, unknown> | null;
  config: Record<string, unknown> | null;
}

export interface ProcessStageGraph {
  id: number;
  code: string;
  name?: string;
  name_key?: string | null;
  names?: LocaleNames;
  name_ru: string;
  name_en: string | null;
  is_active: boolean;
  identification: string | null;
  config: Record<string, unknown> | null;
  flows: ProcessFlowView[];
  operations: ProcessOperationView[];
}

// ---------- orders ----------

export interface OrderAdjustmentRead {
  id: UUID;
  order_id: UUID;
  code: string;
  reason: string | null;
  exception_signals: string[] | null;
  delta_quantity: number;
  created_at: ISODateTime;
}

export interface ProductionOrderRead {
  id: UUID;
  code: string;
  stage_id: number;
  target_quantity: number;
  completed_quantity: number;
  rejected_quantity: number;
  status: OrderStatus;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface ProductionOrderReadDetailed extends ProductionOrderRead {
  adjustments: OrderAdjustmentRead[];
  effective_target: number;
}

export interface ProductionOrderCreate {
  code: string;
  stage_id: number;
  target_quantity: number;
}

export interface ProductionOrderUpdate {
  target_quantity?: number;
  status?: OrderStatus;
}

export interface OrderAdjustmentCreate {
  code: string;
  reason?: string | null;
  exception_signals?: string[] | null;
  delta_quantity: number;
}

// ---------- material items ----------

export interface MaterialItemRead {
  id: UUID;
  code: string;
  name_ru: string | null;
  flow_type: FlowType;
  source: string | null;
  stage_id: number | null;
  batch_id: UUID | null;
  quantity: number | null;
  unit: string | null;
  status: FlowStatus;
  /** rfid_tag/barcode/qr_code are reads via /tags now (universal table);
   * left on the type for back-compat with older frontend code paths
   * that still display them when set. New material items have them = null. */
  rfid_tag: string | null;
  barcode: string | null;
  qr_code: string | null;
  parent_ids: UUID[] | null;
  metadata: Record<string, unknown> | null;
  /** Operator-recorded sample-test results; primary source for passport
   * rendering. Null until PUT /flows/{id}/measurements is called. */
  measured_metrics: Record<string, unknown> | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface MaterialItemCreate {
  code: string;
  name_ru?: string | null;
  flow_type: FlowType;
  source?: string | null;
  stage_id?: number | null;
  batch_id?: UUID | null;
  quantity?: number | null;
  unit?: string | null;
  status?: FlowStatus;
  rfid_tag?: string | null;
  barcode?: string | null;
  qr_code?: string | null;
  parent_ids?: UUID[] | null;
  metadata?: Record<string, unknown> | null;
}

/** PATCH /flows/:id payload — mirrors backend FlowUpdateSchema. Every
 *  field optional + every nullable field accepts null to clear. */
export interface MaterialItemUpdate {
  code?: string;
  name_ru?: string | null;
  flow_type?: FlowType;
  source?: string | null;
  stage_id?: number | null;
  status?: FlowStatus;
  quantity?: number | null;
  unit?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface MaterialItemScan {
  scan_type: ScanType;
  tag_value: string;
  device_id?: string | null;
}

export interface MaterialItemTrace {
  item: MaterialItemRead;
  parents: MaterialItemRead[];
  children: MaterialItemRead[];
}

// ---------- executions ----------

export interface ExecutionRead {
  id: UUID;
  operation_id: UUID;
  order_id: UUID;
  operator_id: UUID | null;
  station_id: string | null;
  input_item_ids: UUID[] | null;
  output_item_ids: UUID[] | null;
  status: ExecutionStatus;
  qc_result: QCResult | null;
  exception_signal: string | null;
  parameters: Record<string, unknown> | null;
  started_at: ISODateTime;
  completed_at: ISODateTime | null;
}

export interface ExecutionStart {
  order_id: UUID;
  operator_id?: UUID | null;
  station_id?: string | null;
  input_item_ids?: UUID[] | null;
  parameters?: Record<string, unknown> | null;
}

export interface ExecutionComplete {
  output_item_ids?: UUID[] | null;
  parameters?: Record<string, unknown> | null;
  units?: number;
}

export interface ExecutionQC {
  qc_result: QCResult;
  exception_signal?: string | null;
  output_item_ids?: UUID[] | null;
  parameters?: Record<string, unknown> | null;
  units?: number;
}

export interface ScanEventRead {
  id: UUID;
  record_id: UUID;
  scan_type: ScanType;
  tag_value: string;
  device_id: string | null;
  scanned_at: ISODateTime;
}

export interface ScanEventInput {
  scan_type: ScanType;
  tag_value: string;
  device_id?: string | null;
}

// ---------- devices ----------

export interface DeviceRead {
  id: UUID;
  code: string;
  type: string;
  protocol: string | null;
  station_id: string | null;
  status: DeviceStatus;
  last_heartbeat: ISODateTime | null;
  config: Record<string, unknown> | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

// ---------- printing ----------

export type LabelType = "PL" | "L" | "PDS" | "CCD" | "CPD" | "TSD";

export interface PrintLabelRequest {
  label_type: LabelType;
  content: Record<string, unknown>;
}

export interface PrintLabelResponse {
  job_id: UUID;
  label_type: LabelType;
  backend: string;
  rendered: string;
  submitted_at: ISODateTime;
}

// ---------- events (WS) ----------

export type EventType =
  | "order.created"
  | "order.status_changed"
  | "order.adjusted"
  | "order.completed"
  | "execution.started"
  | "execution.completed"
  | "execution.qc_passed"
  | "execution.qc_rejected"
  | "scan.recorded"
  | "exception.signal"
  | "device.heartbeat"
  | "device.event";

export interface MesEvent {
  type: EventType | string;
  occurred_at: ISODateTime;
  stage_id: number | null;
  station_id: string | null;
  order_id: UUID | null;
  record_id: UUID | null;
  payload: Record<string, unknown>;
}

// ---------- tags (universal NFC/QR/Barcode/RFID bindings) ----------

export const TagKind = ["NFC", "QR", "BARCODE", "RFID"] as const;
export type TagKind = (typeof TagKind)[number];

export const TagTargetType = [
  "operator",
  "station",
  "device",
  "order",
  "flow_item",
] as const;
export type TagTargetType = (typeof TagTargetType)[number];

export interface TagRead {
  id: UUID;
  kind: TagKind;
  value: string;
  target_type: TagTargetType;
  target_id: string;
  /** Читаемая подпись цели (имя оператора / код станции …); null если удалена. */
  target_label: string | null;
  is_active: boolean;
  created_at: ISODateTime;
  created_by: UUID | null;
  last_seen_at: ISODateTime | null;
}

export interface TagCreate {
  kind: TagKind;
  value: string;
  target_type: TagTargetType;
  target_id: string;
}

export interface ScanLookupRequest {
  kind: TagKind;
  value: string;
}

export interface ScanLookupResponse {
  found: boolean;
  tag_id: UUID | null;
  target_type: TagTargetType | null;
  target_id: string | null;
  summary: string | null;
  entity: Record<string, unknown> | null;
}

// ---------- admin/integrations (ERP outbox) ----------

export type IntegrationOutboxStatus = "pending" | "sent" | "dlq";

export interface IntegrationOutboxRow {
  id: UUID;
  source_system: string;
  event_type: string;
  aggregate_id: string;
  event_seq: number;
  status: IntegrationOutboxStatus;
  attempt_count: number;
  last_error: string | null;
  scheduled_at: ISODateTime;
  sent_at: ISODateTime | null;
  created_at: ISODateTime;
  payload: Record<string, unknown>;
}

export interface IntegrationOutboxStats {
  pending: number;
  sent: number;
  dlq: number;
  oldest_pending_age_sec: number | null;
}

// ---------- STAITER pull-адаптер (приём заказов из STAITER) ----------

export interface ErpPullSettings {
  enabled: boolean;
  base_url: string | null;
  username: string | null;
  /** Пароль наружу не отдаётся — только признак, что он задан. */
  password_set: boolean;
  interval_seconds: number;
  statuses: string;
  page_size: number;
  entity_id: string | null;
  /** Техпроцесс принятых заказов, если событие его не называет. */
  process_key: string | null;
  /** Изделия STAITER (item_id), заказы на которые принимаются; пусто — все. */
  item_ids: string[];
  last_run_at: ISODateTime | null;
  last_success_at: ISODateTime | null;
  last_error: string | null;
  last_checked: number;
  last_created: number;
  last_skipped: number;
  updated_at: ISODateTime;
}

export interface ErpPullSettingsPatch {
  enabled?: boolean;
  base_url?: string | null;
  username?: string | null;
  /** Непустая строка → задать пароль; пусто/опущено → не менять. */
  password?: string | null;
  interval_seconds?: number;
  statuses?: string;
  page_size?: number;
  entity_id?: string | null;
  process_key?: string | null;
  item_ids?: string[];
}

export interface ErpPullCycleResult {
  checked: number;
  created: number;
  skipped: number;
  cancelled: number;
  error: string | null;
}

// ---------- admin/inventory (material rolls + batches) ----------

export type InventoryTracking = "serial" | "batch";

export interface InventoryMaterial {
  id: UUID;
  external_item_id: UUID | null;
  code: string;
  name: string;
  stock_uom: string;
  tracking: InventoryTracking;
  is_active: boolean;
  /** Код справочника material_category. */
  material_category?: string | null;
  specs: Record<string, unknown> | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface InventoryMaterialCreate {
  code: string;
  name: string;
  stock_uom: string;
  tracking: InventoryTracking;
  material_category?: string | null;
  external_item_id?: UUID | null;
  specs?: Record<string, unknown> | null;
}

export interface InventoryMaterialPatch {
  name?: string;
  stock_uom?: string;
  material_category?: string | null;
  tracking?: InventoryTracking;
  is_active?: boolean;
  specs?: Record<string, unknown> | null;
}

export type InventoryBatchQcStatus =
  | "pending"
  | "accepted"
  | "rejected"
  | "quarantine";

export interface InventoryBatch {
  id: UUID;
  material_id: UUID;
  external_batch_id: UUID | null;
  batch_no: string;
  mfg_date: string | null;
  expiry_date: string | null;
  qc_status: InventoryBatchQcStatus | string;
  qty_received: string;
  qty_remaining: string;
  received_at: ISODateTime;
  received_by: UUID | null;
  note: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface InventoryBatchCreate {
  material_id: UUID;
  batch_no: string;
  qty_received: number;
  mfg_date?: string | null;
  expiry_date?: string | null;
  qc_status?: InventoryBatchQcStatus;
  note?: string | null;
  external_batch_id?: UUID | null;
}

export type InventoryRollStatus =
  | "in_stock"
  | "loaded"
  | "depleted"
  | "scrapped";

export interface InventoryRoll {
  id: UUID;
  batch_id: UUID;
  external_serial_id: UUID | null;
  code: string;
  original_qty: string;
  remaining_qty: string;
  current_station_id: number | null;
  loaded_at: ISODateTime | null;
  loaded_by: UUID | null;
  status: InventoryRollStatus | string;
  note: string | null;
  material_id?: UUID;
  material_code?: string;
  batch_no?: string;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface InventoryRollCreate {
  /** Один из двух: material_id (партия создастся автоматом) или batch_id. */
  material_id?: UUID;
  batch_id?: UUID;
  code: string;
  original_qty: number;
  batch_data?: {
    batch_no?: string | null;
    mfg_date?: string | null;
    expiry_date?: string | null;
    qc_status?: InventoryBatchQcStatus;
  };
  note?: string | null;
  external_serial_id?: UUID | null;
}

export interface InventoryConsumptionEvent {
  id: number;
  material_id: UUID;
  batch_id: UUID;
  roll_id: UUID | null;
  production_order_id: UUID | null;
  execution_id: UUID | null;
  qty: string;
  source: "auto" | "manual" | string;
  actor_id: UUID | null;
  at: ISODateTime;
  note: string | null;
}

// ---------- manufacturing orders (заказ на производство) ----------
//
// Заголовочный офисный документ «сделать N элементов артикула X».
// НЕ путать с ProductionOrder (задание на стадию) — это уровень выше.
// При запуске порождает ProductionOrder'ы на готовые стадии 1–3.

export const ManufacturingOrderStatus = [
  "DRAFT",
  "READY",
  "IN_PRODUCTION",
  "COMPLETED",
  "CANCELLED",
] as const;
export type ManufacturingOrderStatus =
  (typeof ManufacturingOrderStatus)[number];

export type ManufacturingOrderSource = "mes" | "staiter";

/** Название на нескольких языках: { ru: "…", en: "…" }. */
export type LocaleName = Record<string, string>;

/** Атрибут заказа из описания техпроцесса. */
export interface OrderAttributeDef {
  key: string;
  label: LocaleName;
  type: "string" | "number" | "integer" | "boolean" | "date";
  unit?: string;
  required_for_ready?: boolean;
  locked_after_launch?: boolean;
  /** Считается сервером (обработчик модуля), в форме только чтение. */
  computed?: boolean;
  default?: unknown;
  /** Показывать колонкой в списке заказов. */
  list_column?: boolean;
  /** Формула расчёта (ключи атрибутов, element_count); офис может заменить значение своим. */
  formula?: string;
}

/** Слот материала заказа из описания техпроцесса. */
export interface MaterialSlotDef {
  slot: string;
  label: LocaleName;
  category?: string;
  required_for_ready?: boolean;
  batch?: boolean;
  locked_after_launch?: boolean;
}

/** Описание заказа техпроцесса (process_definitions.config.order). */
export interface OrderConfig {
  attributes: OrderAttributeDef[];
  materials: MaterialSlotDef[];
  launch_stages?: string[];
  serials: { mode: "mask" | "external" | "none"; mask?: string; scope?: string[] };
  code_mask?: string;
  code_reset?: OrderCounterReset;
  /** Номер партии: назначается при запуске по маске, до запуска задаётся руками. */
  batch_number?: { mask: string; reset: OrderCounterReset };
  handler?: string;
  /** Документы партии: kind — вид шаблона печати, label — название карточки,
   *  copies_attr — атрибут заказа с числом копий по умолчанию. */
  documents?: { kind: string; label: LocaleName; copies_attr?: string }[];
  /** Упаковочный лист изделий техпроцесса (раскладка на паллеты). */
  packing?: Record<string, unknown>;
}

export type OrderCounterReset = "day" | "month" | "year" | "never";

/** PATCH /admin/cycle/processes/{key}/order-attributes — null убирает формулу / значение по умолчанию. */
export interface OrderAttributesPatch {
  attributes: { key: string; formula: string | null; default: unknown }[];
}

export interface OrderProcess {
  key: string;
  name: string;
  order: OrderConfig;
}

export interface ManufacturingOrderMaterial {
  slot: string;
  material_id: UUID | null;
  batch_id: UUID | null;
  material_code: string | null;
  material_name: string | null;
  batch_no: string | null;
}

export interface ManufacturingOrderRead {
  id: UUID;
  code: string;
  status: ManufacturingOrderStatus;
  /** Техпроцесс заказа: его описание задаёт атрибуты, слоты и нумерацию. */
  process_key: string | null;
  source_system: ManufacturingOrderSource | string;
  work_order_ext_id: string | null;

  article: string | null;
  customer_code: string | null;
  /** YYYY-MM-DD — дата заказа. */
  order_date: string | null;
  element_count: number | null;

  /** Числовая часть зарезервированного диапазона номеров (нумерация по маске). */
  serial_from: number | null;
  serial_to: number | null;

  attributes: Record<string, unknown>;
  /** Ключи формульных атрибутов, значение которых введено вручную. */
  manual_attributes: string[];
  materials: ManufacturingOrderMaterial[];
  batch_number: string | null;

  target_metrics: Record<string, unknown>;
  comment: string | null;

  created_by: UUID | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  launched_by: UUID | null;
  launched_at: ISODateTime | null;
}

/** Внешний серийник (нумерация «от заказчика»). */
export interface ManufacturingOrderExternalSerial {
  id: UUID;
  external_serial: string;
  position: number;
  item_id: UUID | null;
}

/** Связанное задание на стадию в детальном ответе заказа. */
export interface ManufacturingOrderRelatedTask {
  id: UUID;
  code: string;
  stage_id: number;
  target_quantity: number;
  completed_quantity: number;
  rejected_quantity: number;
  status: string;
}

export interface ManufacturingOrderDetailed extends ManufacturingOrderRead {
  production_orders: ManufacturingOrderRelatedTask[];
  /** Номер первого изделия по маске техпроцесса. */
  serial_example: string | null;
  /** Внешние серийники (нумерация «от заказчика»). */
  external_serials: ManufacturingOrderExternalSerial[];
}

export interface ManufacturingOrderMaterialInput {
  slot: string;
  material_id: UUID | null;
  batch_id?: UUID | null;
}

export interface ManufacturingOrderCreate {
  code?: string;
  source_system?: ManufacturingOrderSource;
  process_key?: string;
  work_order_ext_id?: string | null;
  article?: string | null;
  customer_code?: string | null;
  order_date?: string | null;
  element_count?: number | null;
  attributes?: Record<string, unknown>;
  materials?: ManufacturingOrderMaterialInput[];
  external_serials?: string[];
  target_metrics?: Record<string, unknown>;
  comment?: string | null;
  batch_number?: string | null;
}

export interface ManufacturingOrderUpdate {
  status?: ManufacturingOrderStatus;
  process_key?: string;
  article?: string | null;
  customer_code?: string | null;
  order_date?: string | null;
  element_count?: number | null;
  /** Правка атрибутов: переданные ключи; null — очистить (формульный — вернуть расчёт). */
  attributes?: Record<string, unknown>;
  materials?: ManufacturingOrderMaterialInput[];
  external_serials?: string[];
  target_metrics?: Record<string, unknown>;
  comment?: string | null;
  /** Только до запуска; null — очистить. */
  batch_number?: string | null;
}

export interface ManufacturingOrderLaunchResponse {
  order: ManufacturingOrderRead;
  created_production_orders: ManufacturingOrderRelatedTask[];
  skipped_stage_codes: string[];
}

/** Стадия короткого пути с её заданием и статусом — чеклист. */
export interface ShortFlowStage {
  stage_code: string;
  stage_id: number;
  production_order_id: UUID;
  status: OrderStatus;
  target_quantity: number;
  completed_quantity: number;
  position: number;
  /** Значения заказа для оператора на этой стадии (stages.config.show_attributes). */
  display: StageDisplayItem[];
  /** Подсказка стадии (stages.config.materials_hint). */
  hint: string | null;
  // Подпись стадии: пустой sign_roles — стадия закрывается в один клик.
  sign_roles: { code: string; name: string }[];
  can_sign: boolean;
  sign_label: string | null;
  checklist: string[];
  checklist_required: boolean;
  measurement_sets: string[];
  release: boolean;
  signoff: StageSignoff | null;
  /** Действующая рецептура техпроцесса (stages.config.show_recipe, модуль
   *  recipes, право recipe.read); null — утверждённой нет. */
  recipe?: StageRecipe | null;
}

/** Компонент рецептуры: массовая доля и допуск ± к ней — в процентах. */
export interface RecipeComponent {
  component: string;
  grade?: string | null;
  function?: string | null;
  share_pct: number;
  tolerance?: number | null;
  spec?: string | null;
}

export interface RecipeParam {
  key: string;
  label: string;
  value: string | null;
  control?: string | null;
}

export interface StageRecipe {
  code: string;
  version: number;
  name: string;
  components: RecipeComponent[];
  params: RecipeParam[];
  approved_at: string | null;
  approver_name: string | null;
}

export interface StageSignoff {
  signer_name: string;
  signer_role: string;
  signer_role_name: string;
  signed_at: string;
  checklist: { text: string; checked: boolean }[];
  comment: string | null;
}

/** Подпись стадии: индексы отмеченных пунктов чек-листа и комментарий. */
export interface StageSignInput {
  checked?: number[];
  comment?: string;
}

/** Причина, по которой стадию нельзя подписать (details.problems ошибки stage_gate). */
export interface GateProblem {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/** Значение заказа на карточке стадии; label уже на языке запроса. */
export interface StageDisplayItem {
  key: string;
  label: string;
  value: string | number | boolean | null;
  unit: string | null;
}

export interface ShortFlowListResponse {
  stages: ShortFlowStage[];
}

export interface ShortFlowAdvanceResponse {
  order: ManufacturingOrderRead;
  stages: ShortFlowStage[];
}

/** Ответ приёмки OEM-партии: сколько изделий создано / уже было. */
export interface ManufacturingOrderReceiveResponse {
  created: number;
  already_present: number;
  items: { id: UUID; external_serial: string; position: number }[];
}

export interface ManufacturingOrderPrintBatchRequest {
  /** Печатать паспорта на каждое принятое изделие (по умолчанию да). */
  passports?: boolean;
  /** Печатать упаковочный лист на партию (по умолчанию да). */
  packing_list?: boolean;
  /** Число копий каждого паспорта (1–10, по умолчанию 1). */
  copies?: number;
}

export interface ManufacturingOrderPrintBatchResponse {
  /** Единое задание печати на весь заказ (один многостраничный PDF —
   *  страница на серийник). null, если паспорта не печатались или ни одно
   *  изделие не удалось скомпоновать. */
  passport_package_job_id: string | null;
  /** Совместимость: по строке на каждое вошедшее изделие, job_id у всех
   *  один — id пакета (passport_package_job_id). */
  passport_jobs: { item_id: UUID; serial: string | null; job_id: string }[];
  packing_list_job_id: string | null;
  errors: { item_id: UUID; error: string }[];
}

/** GET /manufacturing-orders/{id}/documents/{kind} — поля документа партии,
 *  заполненные из заказа (value — из источника плейсхолдера, без него пусто). */
export interface OrderDocumentDraft {
  kind: string;
  template_id: string;
  template_name: string;
  /** readonly — значение из журнала партии: в форме не правится, при печати
   *  берётся из журнала. */
  fields: {
    key: string;
    label: string;
    value: string;
    source: string | null;
    required: boolean;
    readonly: boolean;
  }[];
}

/** Автозаполнение протокола испытаний из заказа на производство. */
export interface TestReportPrefill {
  article: string | null;
  params: Record<string, unknown>;
  serials: string[];
}

export interface ManufacturingOrderPreviewRequest {
  process_key?: string;
  element_count?: number | null;
  attributes?: Record<string, unknown>;
  manual_attributes?: string[];
}

export interface ManufacturingOrderPreviewResponse {
  attributes: Record<string, unknown>;
}

/** Строка справочника установки (reference_items). */
export interface ReferenceItem {
  code: string;
  label: LocaleName;
  sort_order: number;
}

export type ReferenceList = "material_category" | "unit" | "print_kind";

// ── замеры партии (docs/architecture/BATCH_RECORDS.md §6) ──

export interface MeasurementNorm {
  lo: number | null;
  hi: number | null;
  nominal: number | null;
  text: string;
}

/** Показание параметра; норма — на момент замера. */
export interface MeasurementReading {
  param: string;
  value: number | null;
  ok: boolean;
  lo: number | null;
  hi: number | null;
  norm: string;
  note: string | null;
}

export interface MeasurementRound {
  id: UUID;
  round: number;
  ok: boolean;
  measured_at: ISODateTime;
  measurer_name: string;
  measurer_role: string;
  comment: string | null;
  voided: { at: ISODateTime; by_name: string | null; reason: string } | null;
  readings: MeasurementReading[];
}

export interface MeasurementParam {
  key: string;
  label: string;
  type: "number" | "check";
  unit: string | null;
  kind: "product" | "process";
  group: string | null;
  norm: MeasurementNorm;
}

export interface MeasurementSet {
  stage_code: string;
  key: string;
  title: string;
  form: string | null;
  min_rounds: number;
  max_rounds: number | null;
  marks: { ok: string; nok: string };
  /** Можно вносить и аннулировать замеры сейчас (лимит max_rounds — отдельно). */
  editable: boolean;
  params: MeasurementParam[];
  rounds: MeasurementRound[];
  status: "empty" | "incomplete" | "ok" | "nok";
}

export interface MeasurementReadingInput {
  param: string;
  value?: number | null;
  ok?: boolean | null;
  note?: string | null;
}
